import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (authToken) => {
    const tokenToUse = authToken || token;
    if (!tokenToUse) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenToUse}` }
      });
      setUser(response.data);
      // Ensure token is synced in localStorage
      if (tokenToUse && localStorage.getItem('token') !== tokenToUse) {
        localStorage.setItem('token', tokenToUse);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout if it's a 401 (unauthorized) - not for network errors
      if (error.response && error.response.status === 401) {
        // Token is invalid or expired - clear it
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('cartCleared'));
      }
      // For other errors (network issues), keep the user logged in
      // but user will be null until next successful fetch
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser(token);
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('cart'); // Clear cart on logout
    // Dispatch a storage event to notify CartContext
    window.dispatchEvent(new Event('cartCleared'));
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
